/**
 * Route helpers for the /ar/* localised URL space.
 *
 * Contract:
 *   • English is the default and its routes have no prefix ("/", "/track/x").
 *   • Arabic mirrors every route under "/ar" ("/ar", "/ar/track/x").
 *   • Language switch = swap prefix, preserving pathname + search + hash.
 *   • Hashes on the home page (e.g. "/#music") behave the same in Arabic
 *     ("/ar#music") because those anchors are the exact same DOM ids.
 */

export const AR_PREFIX = "/ar";

/** Return `"ar"` if pathname is Arabic-prefixed, else `"en"`. */
export function langFromPath(pathname) {
  if (!pathname) return "en";
  return /^\/ar(\/|$)/.test(pathname) ? "ar" : "en";
}

/** Strip the leading /ar from a pathname (if any). Empty → "/". */
export function stripAr(pathname) {
  if (!pathname) return "/";
  const stripped = pathname.replace(/^\/ar(?=\/|$)/, "") || "/";
  return stripped;
}

/**
 * Return the localised pathname for a given target language.
 * Preserves query string and hash. `to` is the FULL current URL
 * (window.location) or an object shaped like it.
 */
export function localisedUrl(location, lang) {
  const base = stripAr(location.pathname || "/");
  const path = lang === "ar" ? (base === "/" ? "/ar" : `/ar${base}`) : base;
  return `${path}${location.search || ""}${location.hash || ""}`;
}

/**
 * Return `to` string usable inside <Link to={...}> that respects the
 * currently active language. `to` MUST always be an English (unprefixed)
 * path — this helper adds the /ar prefix when the user is browsing Arabic.
 */
export function localisedTo(to, lang) {
  if (!to || typeof to !== "string") return to;
  if (lang !== "ar") return to;
  // Anchor / hash-only links stay as-is
  if (to.startsWith("#")) return to;
  // External links (http, mailto, tel) — no prefix
  if (/^([a-z]+:)?\/\//i.test(to) || /^(mailto|tel):/i.test(to)) return to;
  if (to === "/") return "/ar";
  if (to.startsWith("/ar/") || to === "/ar") return to;
  return `/ar${to.startsWith("/") ? "" : "/"}${to}`;
}
